import { useState, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  IconButton,
  Tooltip,
  useDisclosure,
  VStack,
  Text,
  Box,
  Input,
  useToast,
  HStack,
} from '@chakra-ui/react';

import { AttachmentIcon, ArrowUpIcon } from '@chakra-ui/icons';

import type {
  APILeadUploadData,
  APILeadsListResultData,
} from 'types/api.types';

import { apiLeadFileUpload } from 'api';

const LeadFileUpload = ({
  lead,
  onSuccess,
}: {
  lead: APILeadsListResultData;
  onSuccess?: (data: APILeadUploadData) => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const { mutate, isPending, isError } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      return apiLeadFileUpload(lead.id, formData);
    },
    onSuccess: (data: APILeadUploadData) => {
      toast({
        title: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      setSelectedFile(null);
      onClose();

      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your file',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      mutate(selectedFile);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <>
      <Tooltip label="Upload file" hasArrow placement="top">
        <IconButton
          aria-label="Upload file"
          icon={<AttachmentIcon />}
          size="sm"
          colorScheme="blue"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload File</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              display="none"
            />

            <Box
              border="2px dashed"
              borderColor={isDragging ? 'blue.400' : 'gray.300'}
              borderRadius="md"
              p={6}
              bg={isDragging ? 'blue.50' : 'gray.50'}
              transition="all 0.2s"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              textAlign="center"
              mb={4}
            >
              <VStack spacing={3}>
                <ArrowUpIcon
                  boxSize={10}
                  color={isDragging ? 'blue.500' : 'gray.500'}
                />
                <Text fontWeight="medium">
                  {isDragging
                    ? 'Drop your file here'
                    : 'Drag and drop your file here, or click to select'}
                </Text>
                <Button
                  size="sm"
                  onClick={handleSelectFile}
                  colorScheme="blue"
                  variant="outline"
                >
                  Select File
                </Button>
              </VStack>
            </Box>

            {selectedFile && (
              <Box mt={4} p={3} borderRadius="md" bg="gray.100">
                <HStack justifyContent="space-between">
                  <Text fontWeight="medium" noOfLines={1}>
                    {selectedFile.name}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {(selectedFile.size / 1024).toFixed(0)} KB
                  </Text>
                </HStack>
              </Box>
            )}

            {isError && (
              <Box mt={4} p={3} borderRadius="md" bg="red.100">
                <Text color="red.600">
                  Failed to upload file. Please try again.
                </Text>
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpload}
              isDisabled={!selectedFile || isPending}
              isLoading={isPending}
              loadingText="Uploading"
            >
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LeadFileUpload;
